import React, { useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Plus, Trash2, Upload } from 'lucide-react';
import {
  formatCreatedUpdatedLine,
  formatFileSize,
  getFileVisualClasses,
  getStatusBadge,
  getUploadId,
  DOC_INTEL_UPLOAD_ACCEPT
} from './docIntelUtils';
import { useNowTick } from './useNowTick';

const COLLAPSE_AFTER = 5;

const DocIntelSidebar = ({
  uploads,
  selectedUploadId,
  onSelect,
  onUploadFiles,
  onRetryUpload,
  onDiscardUpload,
  discardingUploadId,
  retryingUploadId,
  isUploading,
  mode = 'deal'
}) => {
  const fileInputRef = useRef(null);
  const now = useNowTick(60_000);
  const [expanded, setExpanded] = useState(false);

  const handleFiles = (files) => {
    if (!files?.length || isUploading) return;
    onUploadFiles(Array.from(files));
  };

  const needsAttention = uploads.filter(
    (u) => u.status === 'review_pending' || u.status === 'failed'
  ).length;
  const scopeLabel = mode === 'template' ? 'this project' : 'this deal';

  const visibleUploads = expanded ? uploads : uploads.slice(0, COLLAPSE_AFTER);
  const hiddenCount = Math.max(0, uploads.length - COLLAPSE_AFTER);

  return (
    <aside className="flex w-full shrink-0 flex-col self-stretch overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.03] lg:w-[18rem]">
      <div className="border-b border-slate-100 bg-gradient-to-r from-teal-50/60 to-white px-3 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-base font-semibold text-slate-900">Uploaded files</p>
            <p className="text-sm font-medium text-slate-600">
              {uploads.length} file{uploads.length === 1 ? '' : 's'} · {scopeLabel}
              {needsAttention > 0 && (
                <span className="ml-1 font-semibold text-amber-800">
                  · {needsAttention} need{needsAttention === 1 ? 's' : ''} action
                </span>
              )}
            </p>
            <p className="mt-0.5 text-xs font-medium text-slate-500">
              File status (Queued → Review → Approved) — not template Draft/Live
            </p>
          </div>
          <button
            type="button"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#2A565E]/20 bg-[#2A565E] text-white shadow-sm transition hover:bg-[#234952] disabled:opacity-50"
            title="Add files">
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" strokeWidth={2.25} />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="sr-only"
            multiple
            accept={DOC_INTEL_UPLOAD_ACCEPT}
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <ul className="max-h-[min(520px,60vh)] flex-1 overflow-y-auto">
        {visibleUploads.map((upload) => {
          const id = getUploadId(upload);
          const badge = getStatusBadge(upload.status);
          const isActive = id === selectedUploadId;
          const visual = getFileVisualClasses(upload.original_filename, upload.mime_type);
          const timeLine = formatCreatedUpdatedLine(upload.created_at, upload.updated_at, now);
          const canDiscard =
            typeof onDiscardUpload === 'function' &&
            upload.status !== 'discarded' &&
            upload.status !== 'applied';

          return (
            <li key={id}>
              <div
                className={`relative border-l-[3px] transition ${
                  isActive
                    ? 'border-l-[#2A565E] bg-teal-50/40'
                    : 'border-l-transparent hover:bg-slate-50/80'
                }`}>
                <button
                  type="button"
                  onClick={() => onSelect(id)}
                  className="w-full px-3 py-3 pr-10 text-left">
                  <div className="flex gap-2.5">
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold ring-1 ${visual.className}`}>
                      {visual.label}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-1">
                        <p
                          className="truncate text-sm font-semibold text-slate-900"
                          title={upload.original_filename}>
                          {upload.original_filename || 'Untitled'}
                        </p>
                        <span
                          className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${badge.className}`}
                          title="Upload / extraction status for this file">
                          {badge.label}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs font-medium text-slate-600">
                        {formatFileSize(upload.size_bytes) || '—'}
                      </p>
                      {timeLine ? (
                        <p
                          className="mt-0.5 text-xs leading-snug text-slate-600"
                          title={timeLine}>
                          {timeLine}
                        </p>
                      ) : null}
                      {upload.status === 'failed' && upload.error_message && (
                        <p className="mt-1 line-clamp-2 text-xs font-medium leading-snug text-red-600">
                          {upload.error_message}
                          {onRetryUpload && (
                            <>
                              {' '}
                              <button
                                type="button"
                                disabled={retryingUploadId === id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRetryUpload(id);
                                }}
                                className="font-semibold text-teal-800 underline decoration-teal-800/40 underline-offset-2 hover:text-teal-900 disabled:opacity-50">
                                {retryingUploadId === id ? 'Retrying…' : 'Retry'}
                              </button>
                            </>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
                {canDiscard && (
                  <button
                    type="button"
                    title="Delete document"
                    disabled={discardingUploadId === id}
                    onClick={(e) => {
                      e.stopPropagation();
                      const name = upload.original_filename || 'this file';
                      const statusNote =
                        upload.status === 'approved' || upload.status === 'applied'
                          ? '\n\nThis file is already approved — deleting it may break the linked template workflow.'
                          : '';
                      if (
                        !window.confirm(
                          `Delete uploaded file “${name}”?${statusNote}\n\nThis cannot be undone.`
                        )
                      ) {
                        return;
                      }
                      onDiscardUpload(id);
                    }}
                    className="absolute right-2 top-3 inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-slate-400 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    aria-label="Delete uploaded file">
                    {discardingUploadId === id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-center gap-1 border-t border-slate-100 bg-slate-50/50 px-3 py-2 text-xs font-bold text-teal-800 hover:bg-slate-100">
          {expanded ? (
            <>
              <ChevronUp className="h-3.5 w-3.5" />
              Show fewer
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" />
              Show {hiddenCount} more
            </>
          )}
        </button>
      )}

      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        className={`mx-3 mb-3 mt-2 cursor-pointer rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 px-3 py-3 text-center transition hover:border-teal-300 hover:bg-teal-50/40 ${
          isUploading ? 'pointer-events-none opacity-60' : ''
        }`}>
        <Upload className="mx-auto mb-1 h-4 w-4 text-teal-700" />
        <p className="text-xs font-medium text-slate-600">Drop files or browse</p>
        <p className="mt-0.5 text-xs font-medium text-slate-500">PDF, JPG, PNG · up to 25 MB</p>
      </div>
    </aside>
  );
};

export default DocIntelSidebar;
